import axios from 'axios';
import qs from 'qs';
import { Mutex } from 'async-mutex';

import type { AxiosError, AxiosResponse } from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  removeAuthTokens,
  setAuthTokens,
} from 'src/helpers/storage';
import { refreshTokens } from 'src/services/auth/refresh-token';
import { SuccessResponse } from 'src/types/api-response';
import { queryClient } from './react-query';
import { authKeys } from '../services/auth/auth-keys';
import store from '../store';
import { clearUserInfo } from '../store/actions/user';

export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: function (params) {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
});

export enum ConfigHeaders {
  /**
   * Do not automatically attach bearer token to header
   */
  NO_BEARER = 'x-no-auto-attach-bearer',
  /**
   * Do not automatically refresh token if an 401 error is thrown
   */
  NO_REFRESH_TOKEN = 'x-no-auto-refresh-token',
  /**
   * Do not automatically reload the site if user is unauthorized (meaning both access and refresh token are invalid)
   */
  NO_RELOAD = 'x-no-auto-reload',
}

apiClient.interceptors.request.use((config) => {
  const accessToken = getAccessToken();

  const absentSkipBearerAttachmentHeader =
    Boolean(config.headers[ConfigHeaders.NO_BEARER]) === false;
  const shouldAttachBearer = absentSkipBearerAttachmentHeader && accessToken;

  if (shouldAttachBearer) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

const handleUnauthorizedError = (shouldReload: boolean, error: AxiosError) => {
  removeAuthTokens();
  const state = store.getState();
  queryClient.invalidateQueries({
    queryKey: authKeys.authenticated(state.user.walletAddress),
  });
  store.dispatch(clearUserInfo());
  // if (shouldReload) {
  //   return location.reload();
  // }

  return Promise.reject(error);
};
const refreshTokenMutex = new Mutex(); // Tạo mutex để quản lý refresh token

apiClient.interceptors.response.use(
  function (response: AxiosResponse<SuccessResponse, unknown>) {
    // If responseType is blob, return the response as-is
    if (response.config.responseType === 'blob') {
      return response;
    }

    return {
      ...response,
      data: response?.data?.data,
    } satisfies AxiosResponse;
  },
  async function (error: AxiosError) {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    const isUnauthorizedError = error.response?.status === 401;
    const hasSkipRefreshTokenHeader = Boolean(error.config?.headers?.[ConfigHeaders.NO_BEARER]);
    const shouldSkipRefreshToken = hasSkipRefreshTokenHeader || !refreshToken || !accessToken;

    const shouldReload = Boolean(error.config?.headers?.[ConfigHeaders.NO_RELOAD]) === false;

    if (!isUnauthorizedError) {
      return Promise.reject(error);
    }

    if (!refreshTokenMutex.isLocked()) {
      const release = await refreshTokenMutex.acquire();

      if (shouldSkipRefreshToken) {
        return handleUnauthorizedError(shouldReload, error);
      }
      try {
        const authTokens = await refreshTokens({ accessToken, refreshToken });
        setAuthTokens(authTokens.data);

        return apiClient.request({
          ...error.config,
          headers: {
            ...error.config?.headers,
            Authorization: `Bearer ${authTokens.data.accessToken}`,
          },
        });
      } catch (RTError) {
        console.error('refresh token Error', RTError);
        handleUnauthorizedError(shouldReload, error);
        location.reload();
      } finally {
        release();
      }
    } else {
      await refreshTokenMutex.waitForUnlock();
      const accessToken = getAccessToken();

      try {
        return apiClient.request({
          ...error.config,
          headers: {
            ...error.config?.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (RTError) {
        handleUnauthorizedError(shouldReload, error);
      }
    }
  },
);
